import { supabase } from './supabase';

export type PlanSlug = 'free' | 'explorer' | 'sensei' | 'concierge' | 'family' | 'agent-50' | 'agent-200' | 'agent-unlimited';

export interface PlanConfig {
  slug: PlanSlug;
  name: string;
  emoji: string;
  priceMonthly: number;
  priceYearly: number;
  aiSuggestions: number;
  aiAnalyses: number;
  features: string[];
  curatedCollections: boolean;
  highlight?: string;
  isPopular?: boolean;
}

export const PLAN_CONFIG: Record<PlanSlug, PlanConfig> = {
  free: {
    slug: 'free',
    name: 'Free',
    emoji: '🗾',
    priceMonthly: 0,
    priceYearly: 0,
    aiSuggestions: 1,
    aiAnalyses: 0,
    features: [],
    curatedCollections: false,
  },
  explorer: {
    slug: 'explorer',
    name: 'Explorer',
    emoji: '🧭',
    priceMonthly: 4.99,
    priceYearly: 39.99,
    aiSuggestions: 5,
    aiAnalyses: 3,
    features: ['feature-export', 'feature-analytics', 'feature-no-ads'],
    curatedCollections: true,
    isPopular: true,
    highlight: 'Most popular',
  },
  sensei: {
    slug: 'sensei',
    name: 'Sensei',
    emoji: '🎓',
    priceMonthly: 9.99,
    priceYearly: 79.99,
    aiSuggestions: 15,
    aiAnalyses: 10,
    features: ['feature-export', 'feature-analytics', 'feature-canvas', 'feature-no-ads'],
    curatedCollections: true,
  },
  concierge: {
    slug: 'concierge',
    name: 'Concierge',
    emoji: '🏯',
    priceMonthly: 29.99,
    priceYearly: 199.99,
    aiSuggestions: 40,
    aiAnalyses: 999,
    features: ['feature-export', 'feature-analytics', 'feature-canvas', 'feature-no-ads'],
    curatedCollections: true,
    highlight: 'Includes personal consultancy',
  },
  'agent-50': {
    slug: 'agent-50',
    name: 'Agent Starter',
    emoji: '💼',
    priceMonthly: 49,
    priceYearly: 468,
    aiSuggestions: 100,
    aiAnalyses: 50,
    features: ['feature-export', 'feature-analytics', 'feature-canvas', 'feature-no-ads', 'feature-agent'],
    curatedCollections: true,
    highlight: 'Up to 50 clients',
  },
  'agent-200': {
    slug: 'agent-200',
    name: 'Agent Pro',
    emoji: '🏢',
    priceMonthly: 99,
    priceYearly: 948,
    aiSuggestions: 350,
    aiAnalyses: 175,
    features: ['feature-export', 'feature-analytics', 'feature-canvas', 'feature-no-ads', 'feature-agent'],
    curatedCollections: true,
    isPopular: true,
    highlight: 'Up to 200 clients',
  },
  'agent-unlimited': {
    slug: 'agent-unlimited',
    name: 'Agent Enterprise',
    emoji: '🌐',
    priceMonthly: 199,
    priceYearly: 1908,
    aiSuggestions: 800,
    aiAnalyses: 400,
    features: ['feature-export', 'feature-analytics', 'feature-canvas', 'feature-no-ads', 'feature-agent'],
    curatedCollections: true,
    highlight: 'Unlimited clients',
  },
  family: {
    slug: 'family',
    name: 'Family',
    emoji: '👨‍👩‍👧‍👦',
    priceMonthly: 39.99,
    priceYearly: 319.99,
    aiSuggestions: 40,
    aiAnalyses: 999,
    features: ['feature-export', 'feature-analytics', 'feature-canvas', 'feature-no-ads'],
    curatedCollections: true,
    highlight: 'Up to 5 travelers',
  },
};

export const ORDERED_PLANS: PlanSlug[] = ['free', 'explorer', 'sensei', 'concierge', 'family', 'agent-50', 'agent-200', 'agent-unlimited'];

export interface UserSubscription {
  planSlug: PlanSlug;
  status: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  stripeSubscriptionId: string | null;
}

export async function getUserSubscription(userId: string): Promise<UserSubscription> {
  const { data: { user } } = await supabase.auth.getUser();
  if (user?.app_metadata?.role === 'admin') {
    return { planSlug: 'concierge', status: 'active', currentPeriodEnd: null, cancelAtPeriodEnd: false, stripeSubscriptionId: null };
  }

  const { data } = await supabase
    .from('subscriptions')
    .select('plan_slug, status, current_period_end, cancel_at_period_end, stripe_subscription_id')
    .eq('user_id', userId)
    .in('status', ['active', 'trialing'])
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (!data) {
    // Check if user is a member of someone's group/family plan
    const { data: membership } = await supabase
      .from('group_members')
      .select('owner_user_id')
      .eq('member_user_id', userId)
      .eq('status', 'accepted')
      .limit(1)
      .maybeSingle();

    if (membership) {
      const { data: ownerSub } = await supabase
        .from('subscriptions')
        .select('plan_slug, status, current_period_end, cancel_at_period_end, stripe_subscription_id')
        .eq('user_id', membership.owner_user_id)
        .in('status', ['active', 'trialing'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (ownerSub) {
        return {
          planSlug: ownerSub.plan_slug as PlanSlug,
          status: ownerSub.status,
          currentPeriodEnd: ownerSub.current_period_end,
          cancelAtPeriodEnd: ownerSub.cancel_at_period_end,
          stripeSubscriptionId: null,
        };
      }
    }

    return { planSlug: 'free', status: null, currentPeriodEnd: null, cancelAtPeriodEnd: false, stripeSubscriptionId: null };
  }

  return {
    planSlug: data.plan_slug as PlanSlug,
    status: data.status,
    currentPeriodEnd: data.current_period_end,
    cancelAtPeriodEnd: data.cancel_at_period_end,
    stripeSubscriptionId: data.stripe_subscription_id,
  };
}

export function planIncludes(planSlug: PlanSlug, featureSlug: string): boolean {
  return PLAN_CONFIG[planSlug]?.features.includes(featureSlug) ?? false;
}

export function planIsAtLeast(userPlan: PlanSlug, requiredPlan: PlanSlug): boolean {
  return ORDERED_PLANS.indexOf(userPlan) >= ORDERED_PLANS.indexOf(requiredPlan);
}

function currentMonthKey(): string {
  // Keep in sync with the suggest-trip edge function bucket key (YYYY-MM).
  // ai_usage.month is TEXT after 2026-04-24; free-tier uses "lifetime",
  // paid tiers use "YYYY-MM". Any other format would miss rows.
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export interface AiQuota {
  suggestionsUsed: number;
  suggestionsLimit: number;
  analysesUsed: number;
  analysesLimit: number;
  canSuggest: boolean;
  canAnalyze: boolean;
}

export async function getAiQuota(userId: string, planSlug: PlanSlug): Promise<AiQuota> {
  const plan = PLAN_CONFIG[planSlug];
  const month = currentMonthKey();

  const { data } = await supabase
    .from('ai_usage')
    .select('suggestions_used, analyses_used')
    .eq('user_id', userId)
    .eq('month', month)
    .maybeSingle();

  const suggestionsUsed = data?.suggestions_used ?? 0;
  const analysesUsed = data?.analyses_used ?? 0;

  let suggestionsLimit = plan.aiSuggestions;
  let effectiveSuggestionsUsed = suggestionsUsed;

  if (planSlug === 'free') {
    const { data: allUsage } = await supabase
      .from('ai_usage')
      .select('suggestions_used')
      .eq('user_id', userId);
    effectiveSuggestionsUsed = (allUsage || []).reduce((sum, r) => sum + (r.suggestions_used || 0), 0);
  }

  return {
    suggestionsUsed: effectiveSuggestionsUsed,
    suggestionsLimit,
    analysesUsed,
    analysesLimit: plan.aiAnalyses,
    canSuggest: effectiveSuggestionsUsed < suggestionsLimit,
    canAnalyze: plan.aiAnalyses === 0 ? false : analysesUsed < plan.aiAnalyses,
  };
}

export interface PlanPricing {
  plan_slug: string;
  display_name: string;
  emoji: string;
  price_monthly: number;
  price_yearly: number;
  stripe_price_id_monthly: string | null;
  stripe_price_id_yearly: string | null;
  highlight: string | null;
  is_popular: boolean;
  is_active: boolean;
  plan_category: 'consumer' | 'agent';
  client_limit: number | null;
  sort_order: number;
}

export async function fetchPlanPricing(): Promise<PlanPricing[]> {
  const { data, error } = await supabase
    .from('plan_pricing')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });

  if (error) {
    console.error('Failed to fetch plan pricing:', error);
    return [];
  }
  return data || [];
}

export function isAgentPlan(planSlug: PlanSlug): boolean {
  return planSlug.startsWith('agent-');
}

export interface BillingProfile {
  vat_number: string | null;
  billing_company_name: string | null;
  billing_address: string | null;
  billing_country_code: string | null;
}

export async function getBillingProfile(userId: string): Promise<BillingProfile | null> {
  const { data } = await supabase
    .from('subscriptions')
    .select('vat_number, billing_company_name, billing_address, billing_country_code')
    .eq('user_id', userId)
    .in('status', ['active', 'trialing'])
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  return (data as BillingProfile) || null;
}

export async function saveBillingProfile(
  userId: string,
  profile: BillingProfile,
): Promise<{ ok: boolean; error?: string }> {
  // Normalise: empty strings to null so the DB length checks don't fire.
  const normalize = (v: string | null) => (v && v.trim() ? v.trim() : null);
  const country = normalize(profile.billing_country_code);
  const payload = {
    vat_number: normalize(profile.vat_number),
    billing_company_name: normalize(profile.billing_company_name),
    billing_address: normalize(profile.billing_address),
    billing_country_code: country ? country.toUpperCase() : null,
  };
  const { error } = await supabase
    .from('subscriptions')
    .update(payload)
    .eq('user_id', userId)
    .in('status', ['active', 'trialing']);
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

export function getAgentClientLimit(planSlug: PlanSlug): number {
  switch (planSlug) {
    case 'agent-50': return 50;
    case 'agent-200': return 200;
    case 'agent-unlimited': return Infinity;
    default: return 0;
  }
}
